/**
 * Sales Log view — table of sales, each with a child `Items` array.
 * The Items column summarises into "<n> items (<qty> units)" with a hover
 * title that lists each item, or shows the single item directly when n === 1.
 *
 * Filterable by date range, platform, and a free-text search across items,
 * buyer, and platform. Filters drive an Export button that opens the same
 * options modal used by Cards/Sealed inventory.
 */
import { DB } from "../lib/db";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { icon } from "../utils/icon";
import { Lists } from "../lib/prefs";
import { escapeHtml } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar } from "../utils/format";
import { onSectionChange } from "../state";
import { exportSalesReport, openExportOptions } from "../components/printReport";
import "../lib/ebaySales";
import { createRowSelection } from "../components/rowSelection";
import type { Row } from "../lib/types";

interface SaleRowView extends Row {
  _subtotal: number;
  _totalCOGS: number;
  /** Compact holding-period label: "Short", "Long", "Mixed", or "Unknown"
   *  depending on what the per-item Tax Treatments say. */
  _holdingLabel: string;
  /** True if any item has Tax Treatment "Unknown" - triggers the warn chip. */
  _hasUnknownHolding: boolean;
}

function normalizeSale(s: Row): SaleRowView {
  const items: any[] = Array.isArray(s.Items) ? s.Items : [];
  (s as SaleRowView)._subtotal  = items.reduce((acc, x) => acc + (Number(x["Sale Price"]) || 0), 0);
  (s as SaleRowView)._totalCOGS = items.reduce((acc, x) => acc + (Number(x["COGS"]) || 0), 0);
  const treatments = new Set(items.map(x => x["Tax Treatment"]).filter(Boolean));
  let label = "-";
  let hasUnknown = false;
  if (treatments.size === 0) {
    label = "-";
  } else if (treatments.has("Unknown")) {
    hasUnknown = true;
    label = treatments.size === 1 ? "Unknown" : "Mixed";
  } else if (treatments.size === 1) {
    label = treatments.has("Long-term") ? "Long" : "Short";
  } else {
    label = "Mixed";
  }
  (s as SaleRowView)._holdingLabel = label;
  (s as SaleRowView)._hasUnknownHolding = hasUnknown || !!s["Has Unknown Holding"];
  return s as SaleRowView;
}

function summarizeSaleItems(items: any[] | undefined): string {
  if (!Array.isArray(items) || !items.length) return `<span class="text-[var(--muted)]">—</span>`;
  if (items.length === 1) {
    const x = items[0];
    return escapeHtml((x.Item || x["Lot ID"] || "?") + ((x.Qty || 1) > 1 ? ` ×${x.Qty}` : ""));
  }
  const totalQty = items.reduce((s, x) => s + (Number(x.Qty) || 1), 0);
  const titles = items.map(x => `${x.Item || x["Lot ID"] || "?"}${(x.Qty || 1) > 1 ? ` ×${x.Qty}` : ""}`).join("\n");
  return `<span title="${escapeHtml(titles)}"><b>${items.length}</b> items <span class="text-[var(--muted)]">(${totalQty} units)</span></span>`;
}

export const SalesFilters = { search: "", platform: "", from: "", to: "", status: "all" };
onSectionChange.push((_prev, _next) => { SalesFilters.search = ""; });
(window as any).SalesFilters = SalesFilters;

function _saleStatusOf(r: Row): string { return String(r["Status"] || "Completed"); }
function _matchesStatusFilter(filter: string, status: string): boolean {
  if (filter === "all") return true;
  if (filter === "draft") return status === "Draft";
  if (filter === "completed") return status === "Completed";
  return true;
}
export function setSalesStatusFilter(value: string): void {
  SalesFilters.status = value;
  (window as any).render?.();
}
(window as any).setSalesStatusFilter = setSalesStatusFilter;

/** Apply the visible filter chain to the raw sales table. Used by both the
 *  on-screen render and the Export button.
 */
function applySalesFilters(): Row[] {
  const f = SalesFilters;
  let rows = DB.table("sales").slice().reverse();
  if (f.search) {
    const q = f.search.toLowerCase();
    rows = rows.filter(r => {
      if (Object.values(r).some(v => String(v || "").toLowerCase().includes(q))) return true;
      if (Array.isArray(r.Items) && r.Items.some((it: any) => Object.values(it).some(v => String(v || "").toLowerCase().includes(q)))) return true;
      return false;
    });
  }
  if (f.platform) rows = rows.filter(r => r["Platform"] === f.platform);
  if (f.from)     rows = rows.filter(r => (r["Date"] || "") >= f.from);
  if (f.to)       rows = rows.filter(r => (r["Date"] || "") <= f.to);
  rows = rows.filter(r => _matchesStatusFilter(f.status || "all", _saleStatusOf(r)));
  return rows;
}

export function exportSalesReportFromView(): void {
  openExportOptions("Sales", (format, detail) => {
    const rows = applySalesFilters();
    exportSalesReport(format, detail, rows, SalesFilters);
  });
}
(window as any).exportSalesReportFromView = exportSalesReportFromView;

const sel = createRowSelection("sales");
function salesEdit(id: string): void { (window as any).editSale?.(id); }
function salesDelete(id: string): void { (window as any).deleteRow?.("sales", "Sale ID", id); }

export function viewSales(root: HTMLElement): void {
  const f = SalesFilters;
  const sales = applySalesFilters().map(normalizeSale);
  // Financial KPIs reflect Completed sales only; drafts are excluded from money totals.
  const completedSales = sales.filter(s => _saleStatusOf(s) === "Completed");
  const totalSubtotal = sum(completedSales, s => s._subtotal);
  const totalFees     = sum(completedSales, s => (Number(s["Fees"]) || 0) + (Number(s["Shipping"]) || 0));
  const totalProfit   = sum(completedSales, s => Number(s["Net Profit"]) || 0);

  // Distinct platforms for the filter dropdown — pulled from Lists + actual data.
  const platformsFromData = Array.from(new Set(DB.table("sales").map(s => s["Platform"]).filter(Boolean))) as string[];
  const platforms = Array.from(new Set([...Lists.get("Sale Platform"), ...platformsFromData]));

  const sales_total = sales.length;
  const sales_page = slicePage("sales", sales, 50);
  root.innerHTML = `
    ${kpiBar([
      { label: "Sales",       value: fmtN(sales.length) },
      { label: "Gross Sales", value: fmt$0(totalSubtotal) },
      { label: "Fees + Ship", value: fmt$0(totalFees) },
      { label: "Net Profit",  value: fmt$0(totalProfit), tone: totalProfit >= 0 ? "green" : "red" },
    ])}

    <div class="panel p-3 mb-3 flex flex-wrap gap-2 items-center">
      <div class="flex flex-wrap gap-1 mr-2">
        <button type="button" class="pill ${f.status === "all" ? "pill-blue" : "pill-gray"} text-xs cursor-pointer hover:opacity-90" onclick="setSalesStatusFilter('all')">All <span class="opacity-70">(${DB.table("sales").length})</span></button>
        <button type="button" class="pill ${f.status === "draft" ? "pill-blue" : "pill-gray"} text-xs cursor-pointer hover:opacity-90" onclick="setSalesStatusFilter('draft')">Drafts <span class="opacity-70">(${DB.table("sales").filter(r => _saleStatusOf(r) === "Draft").length})</span></button>
        <button type="button" class="pill ${f.status === "completed" ? "pill-blue" : "pill-gray"} text-xs cursor-pointer hover:opacity-90" onclick="setSalesStatusFilter('completed')">Completed <span class="opacity-70">(${DB.table("sales").filter(r => _saleStatusOf(r) === "Completed").length})</span></button>
      </div>
      <div class="relative flex-1 min-w-[180px]">
        <input class="input w-full pr-8"
               placeholder="Search items, buyer, platform, sale ID…"
               value="${escapeHtml(f.search)}"
               oninput="SalesFilters.search=this.value;render()"
               data-focus-key="sales-search">
        ${f.search ? `<button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-white text-sm leading-none" onmousedown="event.preventDefault()" onclick="SalesFilters.search='';render()" title="Clear search">✕</button>` : ""}
      </div>
      <select class="select !w-36" title="Filter by platform" onchange="SalesFilters.platform=this.value;render()">
        <option value="">All platforms</option>
        ${platforms.map(o => `<option ${f.platform === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}
      </select>
      <input class="input !w-36" type="date" title="From date" value="${escapeHtml(f.from)}" oninput="SalesFilters.from=this.value;render()">
      <input class="input !w-36" type="date" title="To date" value="${escapeHtml(f.to)}" oninput="SalesFilters.to=this.value;render()">
      ${(f.search || f.platform || f.from || f.to)
        ? `<button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="SalesFilters.search='';SalesFilters.platform='';SalesFilters.from='';SalesFilters.to='';render()">Clear</button>`
        : ""}
      <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="exportSalesReportFromView()" title="Export currently-visible rows as a printable PDF or CSV">🖨 Export</button>
      <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="syncEbayOrders()" title="Check eBay for sold orders not yet recorded as sales">⇩ eBay Sales</button>
      ${sel.toolbarHtml([
        { id: "edit",   label: `${icon("edit")} Edit`,   title: "Edit selected row", handler: salesEdit },
        { id: "delete", label: `${icon("trash")} Delete`, title: "Delete selected row", danger: true, handler: salesDelete },
      ])}
    </div>

    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th>Sale ID</th><th>Date</th><th>Status</th><th>Items</th><th>Platform</th><th>Buyer</th>
          <th class="text-right">Subtotal</th><th class="text-right">Fees</th><th class="text-right">Ship</th>
          <th class="text-right">COGS</th><th class="text-right">Net</th>
          <th title="Short-term (<=365d) vs Long-term (>365d) classification across the sold items">Hold</th>
          <th></th>
        </tr></thead>
        <tbody>
          ${sales_page.map(s => {
            const id = escapeHtml(s["Sale ID"] || "");
            const warn = s._hasUnknownHolding
              ? `<span class="pill pill-amber ml-1" title="One or more items has no Acq Date - holding period unknown. Fix the source lot for accurate short/long-term classification.">!</span>`
              : "";
            const pillColor = s._holdingLabel === "Long" ? "blue"
                            : s._holdingLabel === "Short" ? "amber"
                            : s._holdingLabel === "Mixed" ? "violet"
                            : "gray";
            const st = _saleStatusOf(s);
            const isDraft = st === "Draft";
            const statusPill = isDraft ? `<span class="pill pill-amber text-xs">Draft</span>` : `<span class="pill pill-blue text-xs">Completed</span>`;
            return `<tr ${sel.rowAttrs(id, `editSale('${id}')`)} class="${isDraft ? "opacity-80" : ""}">
              <td class="font-mono text-xs"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${id}')">${id}</a>${warn}</td>
              <td>${escapeHtml(fmtDate(s["Date"]))}</td>
              <td>${statusPill}</td>
              <td class="text-xs">${summarizeSaleItems(s.Items)}</td>
              <td>${escapeHtml(s["Platform"] || "")}</td>
              <td class="text-xs">${escapeHtml(s["Buyer"] || "")}</td>
              <td class="text-right stat-num">${fmt$(s._subtotal)}</td>
              <td class="text-right stat-num">${fmt$(s["Fees"])}</td>
              <td class="text-right stat-num">${fmt$(s["Shipping"])}</td>
              <td class="text-right stat-num">${isDraft ? '<span class="text-[var(--muted)]">&mdash;</span>' : fmt$(s._totalCOGS)}</td>
              <td class="text-right stat-num">${isDraft ? '<span class="text-[var(--muted)]">&mdash;</span>' : fmt$(s["Net Profit"])}</td>
              <td class="text-xs">${s._holdingLabel === "-" ? `<span class="text-[var(--muted)]">-</span>` : `<span class="pill pill-${pillColor}">${s._holdingLabel}</span>`}</td>
              <td class="text-right whitespace-nowrap">${isDraft ? `<button class="btn btn-primary !py-0.5 !px-2 text-xs" onclick="event.stopPropagation(); editSale('${id}')" title="Open the draft to finish and promote to Completed">Open</button>` : ""}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="13" class="text-center text-[var(--muted)] py-8">No sales match your filters.</td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("sales", sales_total, 50)}
  `;
  sel.applyUI();
}
